/* V160 — Admin Funnels Tab.
   Single hub showing every public funnel / landing / demo URL.
   Click any link → opens in new tab. Includes copy-to-clipboard for sharing.

   V230 — Adds conversion-metrics header pulling from
   `/api/admin/funnels` (visits, checkout started, paid, conversion %,
   revenue € — scoped to a trailing N-day window). The URL registry
   below is left intact as the quick-navigation footer.
*/
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../config";

const BASE = (typeof window !== "undefined" ? window.location.origin : "");

const GROUPS = [
  {
    label: "🏠 Acquisition · Landing & Pricing",
    color: "border-emerald-500",
    items: [
      { url: "/", title: "Homepage", desc: "Main landing · hero + scan form" },
      { url: "/#pricing", title: "Pricing anchor", desc: "75% off banner" },
      { url: "/#seo-tools", title: "Tools section", desc: "Free tools showcase" },
      { url: "/pricing", title: "Pricing page", desc: "Full plan comparison" },
      { url: "/blog", title: "Blog", desc: "SEO content hub" },
    ],
  },
  {
    label: "📢 Ad Campaigns · /reklama/ai-agenti",
    color: "border-amber-500",
    note: "Animovaná reklama. queue=1 spustí batch recording všetkých 18 variantov.",
    items: [
      { url: "/reklama/ai-agenti?queue=1", title: "🎬 BATCH RECORD (18 variants)", desc: "1 klik → 18 MP4 ~14 min", highlight: true },
      // 6 languages × 3 aspects = 18
      ...(["sk", "cs", "en", "de", "pl", "hu"].flatMap((lang) =>
        ["9-16", "1-1", "16-9"].map((aspect) => ({
          url: `/reklama/ai-agenti?lang=${lang}&aspect=${aspect}`,
          title: `${lang.toUpperCase()} · ${aspect}`,
          desc: `Variant B copy · ${aspect}`,
        }))
      )),
    ],
  },
  {
    label: "🤖 AI Agent Demos · 6 live",
    color: "border-purple-500",
    note: "Interaktívne demo stránky pre každého live agenta. Linked z /agents marketplace.",
    items: [
      { url: "/agent-os/demo/helpdesk", title: "Helpdesk Agent", desc: "€99/mo · branded support replies" },
      { url: "/agent-os/demo/review-reply", title: "Review Reply Agent", desc: "€79/mo · Google/Heureka/Trustpilot" },
      { url: "/agent-os/demo/cart-recovery", title: "Cart Recovery Agent", desc: "€99/mo · 12-18% recovery rate" },
      { url: "/agent-os/demo/product-desc", title: "Product Desc Optimizer", desc: "€79/mo · bulk SEO rewrites" },
      { url: "/agent-os/demo/newsletter", title: "Newsletter Agent", desc: "€79/mo · weekly AI newsletters" },
      { url: "/agent-os/demo/gdpr-watch", title: "GDPR + A11y Watch", desc: "€49/mo · daily compliance scan" },
      { url: "/agent-os/eshop", title: "🛍️ Marketplace", desc: "All 6 agents · bundle 3-for-2 · 14-day trial" },
      { url: "/agents", title: "Agents landing", desc: "Public agents catalog page" },
    ],
  },
  {
    label: "🎯 Lead-Gen Funnels",
    color: "border-pink-500",
    items: [
      { url: "/free-ads-preview", title: "AI Ads Preview", desc: "FREE · 3 Google Ad variants in 10s" },
      { url: "/ai-simulator", title: "AI Simulator", desc: "FREE · ChatGPT brand mention test" },
      { url: "/cookie-audit", title: "Cookie / GDPR Audit", desc: "FREE · compliance check" },
      { url: "/revenue-leak", title: "Revenue Leak Audit", desc: "€47 · 10 conversion leaks + plan" },
      { url: "/fix-my-seo", title: "Fix My SEO", desc: "€9 · one-time pro audit" },
    ],
  },
  {
    label: "🧠 Paid SaaS · Monthly",
    color: "border-blue-500",
    items: [
      { url: "/ai-intelligence", title: "AI Intelligence Bundle", desc: "€39/mo · prompts + citations + sentiment" },
      { url: "/brand-monitor", title: "Brand Monitor", desc: "€19/mo · track brand across AI engines" },
      { url: "/competitor-spy", title: "Competitor Spy", desc: "€29/mo · watch competitor rankings" },
      { url: "/attribution", title: "AI Attribution", desc: "€39/mo · know which AI drives sales" },
      { url: "/seo-autopilot", title: "SEO Autopilot", desc: "€79/mo · auto-fix SEO 24/7" },
      { url: "/ads-creator", title: "PulseStudio", desc: "€69/mo · Ads, Reels, Photo → AI" },
      { url: "/editor", title: "Editor showcase", desc: "FREE · ads editor demo" },
      { url: "/blog-agent", title: "Blog Agent", desc: "€25 · autopilot blog writing" },
    ],
  },
  {
    label: "🔌 Plugins & Integrations",
    color: "border-teal-500",
    items: [
      { url: "/plugin", title: "WordPress plugin", desc: "FREE · 1-click install" },
      { url: "/plugin#woocommerce", title: "WooCommerce", desc: "Embed scan on shop" },
      { url: "/plugin#shopify", title: "Shopify", desc: "Embed scan on store" },
    ],
  },
  {
    label: "👤 User-facing Dashboards (after login)",
    color: "border-indigo-500",
    note: "Funnely pre platených klientov. Workspace ID je dynamický.",
    items: [
      { url: "/dashboard", title: "User Dashboard", desc: "Po-login hub · scans, plans, agenti" },
      { url: "/agent-os/leaderboard", title: "Top Referrers", desc: "Gamified leaderboard" },
      { url: "/login", title: "Login page", desc: "Email + Google OAuth" },
      { url: "/signup", title: "Signup page", desc: "Account creation funnel" },
    ],
  },
];

export default function AdminFunnelsTab() {
  const [filter, setFilter] = useState("");
  const [copied, setCopied] = useState("");

  // V230 — Conversion metrics (loaded on mount + on window change)
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState("");
  const [days, setDays] = useState(30);

  const adminToken = useMemo(() => {
    if (typeof window === "undefined") return "";
    return (
      sessionStorage.getItem("admin_token") ||
      sessionStorage.getItem("trp_admin_token") ||
      ""
    );
  }, []);

  useEffect(() => {
    let aborted = false;
    const load = async () => {
      if (!adminToken) {
        setMetricsError("Zadaj admin token (ulož ho do sessionStorage pod kľúčom 'admin_token').");
        return;
      }
      setMetricsLoading(true);
      setMetricsError("");
      try {
        const r = await axios.get(`${API}/admin/funnels?days=${days}`, {
          headers: { "X-Admin-Token": adminToken },
        });
        if (!aborted) setMetrics(r.data);
      } catch (err) {
        if (!aborted) setMetricsError(err?.response?.data?.detail || err.message);
      } finally {
        if (!aborted) setMetricsLoading(false);
      }
    };
    load();
    return () => { aborted = true; };
  }, [adminToken, days]);

  const filteredGroups = useMemo(() => {
    if (!filter.trim()) return GROUPS;
    const q = filter.toLowerCase();
    return GROUPS.map((g) => ({
      ...g,
      items: g.items.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          i.url.toLowerCase().includes(q) ||
          (i.desc || "").toLowerCase().includes(q)
      ),
    })).filter((g) => g.items.length > 0);
  }, [filter]);

  const totalCount = GROUPS.reduce((s, g) => s + g.items.length, 0);
  const visibleCount = filteredGroups.reduce((s, g) => s + g.items.length, 0);

  const copy = async (url) => {
    const full = `${BASE}${url}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(url);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      window.prompt("Skopíruj URL:", full);
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="admin-funnels-tab">
      {/* V230 — Conversion metrics header */}
      <section className="bg-zinc-950 border border-zinc-800 p-4" data-testid="funnels-metrics-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            📊 Konverzie podľa funnelu
          </h2>
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-500">Rozsah:</label>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="bg-zinc-900 border border-zinc-700 text-xs px-2 py-1 text-white"
              data-testid="funnels-metrics-days"
            >
              <option value={7}>7 dní</option>
              <option value={30}>30 dní</option>
              <option value={90}>90 dní</option>
              <option value={180}>180 dní</option>
              <option value={365}>365 dní</option>
            </select>
            <a
              href="/api/static/embed/funnels.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] bg-amber-500 hover:bg-amber-400 text-black font-bold px-2 py-1"
              data-testid="funnels-fullscreen-link"
            >
              Fullscreen ↗
            </a>
          </div>
        </div>

        {metricsLoading && (
          <p className="text-xs text-zinc-400">Načítavam konverzie…</p>
        )}
        {metricsError && !metricsLoading && (
          <p className="text-xs text-rose-400" data-testid="funnels-metrics-error">
            {metricsError}
          </p>
        )}

        {metrics && !metricsLoading && (
          <>
            {/* KPI totals */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
              <KpiBox label="Visits" value={fmtInt(metrics.totals?.visits)} />
              <KpiBox label="Started" value={fmtInt(metrics.totals?.checkout_started)} />
              <KpiBox label="Waitlist" value={fmtInt(metrics.totals?.waitlist_signups)} />
              <KpiBox label="Paid" value={fmtInt(metrics.totals?.paid)} />
              <KpiBox label="Conv %" value={(metrics.totals?.conversion_pct || 0).toFixed(2) + "%"} />
              <KpiBox label="Revenue" value={fmtEur(metrics.totals?.revenue_eur)} highlight />
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="funnels-metrics-table">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-500">
                    <th className="text-left py-2 pr-2">Funnel</th>
                    <th className="text-left py-2 pr-2">Landing</th>
                    <th className="text-right py-2 px-2">Visits</th>
                    <th className="text-right py-2 px-2">Started</th>
                    <th className="text-right py-2 px-2">Waitlist</th>
                    <th className="text-right py-2 px-2">Paid</th>
                    <th className="text-right py-2 px-2">Conv %</th>
                    <th className="text-right py-2 px-2">Revenue</th>
                    <th className="text-left py-2 pl-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(metrics.rows || []).map((r) => (
                    <tr key={r.id} className="border-b border-zinc-900 hover:bg-zinc-900/40"
                        data-testid={`metrics-row-${r.id}`}>
                      <td className="py-2 pr-2 font-semibold text-white">{r.name}</td>
                      <td className="py-2 pr-2">
                        <a href={r.landing} target="_blank" rel="noopener noreferrer"
                           className="text-amber-400 font-mono hover:underline">
                          {r.landing}
                        </a>
                      </td>
                      <td className="py-2 px-2 text-right">{fmtInt(r.visits)}</td>
                      <td className="py-2 px-2 text-right">{fmtInt(r.checkout_started)}</td>
                      <td className="py-2 px-2 text-right">{fmtInt(r.waitlist_signups)}</td>
                      <td className="py-2 px-2 text-right">{fmtInt(r.paid)}</td>
                      <td className={`py-2 px-2 text-right ${convClass(r.conversion_pct)}`}>
                        {(r.conversion_pct || 0).toFixed(2)}%
                      </td>
                      <td className="py-2 px-2 text-right font-semibold text-emerald-400">
                        {fmtEur(r.revenue_eur)}
                      </td>
                      <td className="py-2 pl-2">
                        <StatusPill status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          🔗 Všetky URL na funnely
        </h2>
        <input
          type="text"
          placeholder="Hľadať (názov, URL alebo popis)..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-700 text-xs px-3 py-2 text-white w-72"
          data-testid="funnels-filter-input"
        />
      </div>

      <div className="text-xs text-zinc-500">
        Zobrazené {visibleCount} z {totalCount} URL · BASE: <span className="text-amber-400 font-mono">{BASE}</span>
      </div>

      {filteredGroups.length === 0 && (
        <p className="text-zinc-500 text-sm">Žiadne URL nezodpovedajú filtru.</p>
      )}

      <div className="space-y-4">
        {filteredGroups.map((g) => (
          <section
            key={g.label}
            className={`bg-zinc-950 border-l-4 ${g.color} border-r border-y border-zinc-800 p-3`}
            data-testid={`funnel-group-${g.label.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
          >
            <h3 className="font-bold text-white text-sm mb-1">{g.label}</h3>
            {g.note && <p className="text-[11px] text-zinc-500 mb-2">{g.note}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mt-2">
              {g.items.map((it) => (
                <div
                  key={it.url}
                  className={`flex items-center gap-2 bg-zinc-900 border border-zinc-800 p-2 hover:border-amber-500 transition-colors ${
                    it.highlight ? "border-amber-500 bg-amber-500/5" : ""
                  }`}
                  data-testid={`funnel-row-${it.url.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase().slice(0, 60)}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-sm truncate">{it.title}</div>
                    <div className="text-[11px] text-zinc-300 truncate">{it.desc}</div>
                    <div className="text-[11px] text-amber-300 font-mono truncate">{it.url}</div>
                  </div>
                  <button
                    onClick={() => copy(it.url)}
                    className="text-[10px] bg-zinc-800 hover:bg-zinc-700 px-2 py-1 text-white"
                    title="Kopírovať do clipboardu"
                  >
                    {copied === it.url ? "✓" : "📋"}
                  </button>
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] bg-amber-500 hover:bg-amber-400 text-black font-bold px-2 py-1"
                  >
                    ↗
                  </a>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <p className="text-[10px] text-zinc-600 pt-2 border-t border-zinc-800">
        Tip: Klikni 📋 pre rýchle kopírovanie linky do clipboardu (využiteľné v emaile, Slacku alebo Messengeri).
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// V230 — Conversion metrics helpers
// ────────────────────────────────────────────────────────────────────
function fmtInt(n) {
  return Number(n || 0).toLocaleString("sk-SK");
}
function fmtEur(n) {
  return "€" + Number(n || 0).toLocaleString("sk-SK", { maximumFractionDigits: 2 });
}
function convClass(pct) {
  if (pct >= 3) return "text-emerald-400";
  if (pct >= 1) return "text-amber-400";
  return "text-zinc-500";
}

function KpiBox({ label, value, highlight = false }) {
  return (
    <div className={`bg-zinc-900 border p-2 ${highlight ? "border-emerald-500/40" : "border-zinc-800"}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <div className={`text-base font-bold ${highlight ? "text-emerald-400" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active:     { cls: "bg-emerald-500/15 text-emerald-400", label: "ACTIVE" },
    pre_launch: { cls: "bg-purple-500/15 text-purple-400",   label: "PRE-LAUNCH" },
    no_traffic: { cls: "bg-zinc-700/40 text-zinc-500",       label: "NO TRAFFIC" },
  };
  const s = map[status] || map.no_traffic;
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold ${s.cls}`}>
      {s.label}
    </span>
  );
}
